export function ordersEnabled(vertical: { slug: string; settings?: Record<string, unknown> | null } | undefined): boolean {
  if (!vertical) return false;
  return typeof vertical.settings?.orders_enabled === 'boolean'
    ? vertical.settings.orders_enabled
    : ['homeowners', 'homes', 'lighting', 'dealers'].includes(vertical.slug);
}

export function orderCareTeams<T extends { id: number }>(crm: T[] = [], support: T[] = []): T[] {
  // A minimal care grant must not require opening the team's sales CRM.
  return [...new Map([...support, ...crm].map(team => [team.id, team])).values()];
}

export function feedbackRoutingPayload(routes: Record<string, { vertical_id?: number; assignee_user_id?: number }>) {
  return Object.fromEntries(Object.entries(routes).filter(([, route]) => {
    if (route.vertical_id === undefined && route.assignee_user_id === undefined) return false;
    if (!Number.isSafeInteger(route.vertical_id) || (route.vertical_id ?? 0) <= 0) throw new Error('Choose a support team for each configured feedback route.');
    if (route.assignee_user_id !== undefined && (!Number.isSafeInteger(route.assignee_user_id) || route.assignee_user_id <= 0)) throw new Error('Feedback assignee must be a positive user ID.');
    return true;
  }));
}

export function isWonStage(stage: string | null | undefined, configuredStages: unknown = []): boolean {
  const normalized = (stage ?? '').trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
  return normalized === 'won' || normalized === 'closed won'
    || (Array.isArray(configuredStages) && configuredStages.some(value => typeof value === 'string' && value === stage));
}

export function isLostStage(stage: string | null | undefined, configuredStages?: unknown): boolean {
  if (Array.isArray(configuredStages)) return configuredStages.some(value => typeof value === 'string' && value === stage);
  // Preserve the board's legacy aliases only when no lost-stage list exists.
  return ['lost', 'closed', 'closed-lost'].includes((stage ?? '').trim().toLowerCase());
}

export function blocksOrderClosure(nextPhase: string, order: {
  order_number?: string | null;
  installed_at?: string | null;
  site_phase?: string | null;
  feedback_submitted_at?: string | null;
  feedback_confirmed_at?: string | null;
} | null | undefined): boolean {
  // An installation marker alone does not turn a legacy deal into an order.
  // Conversely, changing phase or disabling booking must not bypass an existing order's gate.
  return nextPhase === 'closed' && !!order?.order_number?.trim()
    && !!(order.installed_at || order.site_phase === 'installed') && (!order.feedback_submitted_at || !order.feedback_confirmed_at);
}

function customerContactError(customer: { customer_name: string; customer_phone: string; customer_email: string }, requireEmail: boolean): string {
  if (!customer.customer_name.trim()) return 'Customer name is required.';
  if (!/^\+[1-9]\d{7,14}$/.test(customer.customer_phone.trim())) return 'Enter the customer phone in E.164 format, e.g. +919876543210.';
  if ((requireEmail || customer.customer_email.trim()) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.customer_email.trim())) return 'Enter a valid customer email.';
  return '';
}

export const bookingError = (customer: Parameters<typeof customerContactError>[0]): string => customerContactError(customer, true);
export const unmatchedFeedbackError = (customer: Parameters<typeof customerContactError>[0]): string => customerContactError(customer, false);

export function installedAge(value?: string | null): string {
  const time = value ? Date.parse(value) : NaN;
  return Number.isFinite(time) ? `${Math.max(0, Math.floor((Date.now() - time) / 86400000))} days since installation` : 'Installation date not recorded';
}
